import {
    Badge,
    Box,
    CircularProgress,
    Grid,
    Link,
    Typography,
} from '@deere/fuel-react';
import { CheckCircle } from '@mui/icons-material';
import { Breadcrumbs } from '@mui/material';
import { DateTime } from 'luxon';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertBox } from '../../components/AlertBox';
import jobStyles from '../../components/JobDetails.module.css';
import { WithdrawBidModal } from '../../components/WithdrawBidModal';
import { LoggedInContext } from '../../context/LoggedInContext';
import { errorBannerFromResponse } from '../../utils/errorBanner';
import styles from '../Admin/ActiveJobDetails.module.css';

export const WorkforceMyJobBidsWithdraw = () => {
    const { jobNumber } = useParams();
    const [job, setJob] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [alertMsg, setAlertMsg] = useState();
    const [alertVariant, setAlertVariant] = useState(false);
    const { makeRequest, authState } = useContext(LoggedInContext);
    useEffect(() => {
        let ignore = false;
        const fetchActiveSelectedBid = async () => {
            setIsLoading(true);
            try {
                if (!ignore) {
                    const jobResponse = await makeRequest(
                        `${process.env.REACT_APP_API_ENDPOINT}/jobs/activeSelected/${jobNumber}`
                    );
                    if (jobResponse.status === 200) {
                        const jobDetails = await jobResponse.json();
                        if (jobDetails.status !== 'WITHDRAWN') {
                            setAlertMsg(
                                'You can cancel your bid on this job by clicking on the button below.'
                            );
                            setAlertVariant('info');
                        }
                        setJob(jobDetails);
                    } else {
                        setAlertMsg(
                            errorBannerFromResponse(
                                jobResponse,
                                'We are unable to retrieve this job bid. Please try again later.'
                            ).message
                        );
                        setAlertVariant('error');
                    }
                    setIsLoading(false);
                }
            } catch (err) {
                setAlertMsg(
                    errorBannerFromResponse(
                        err,
                        'We are unable to retrieve this job bid. Please try again later.'
                    ).message
                );
                setAlertVariant('error');
            }
        };

        fetchActiveSelectedBid();

        // cleanup function that will ignore future results of fetch triggered by the useEffect hook
        return () => {
            ignore = true;
        };
    }, [jobNumber, authState, makeRequest]);

    return (
        <div className={styles.pageLevelParent}>
            <Box spacing={6} className={styles.pageLevelContent}>
                <Breadcrumbs aria-label="Breadcrumbs">
                    <Link href="/my-job-bids">My Current Job Bids</Link>
                    <Typography color="textPrimary">Job {jobNumber}</Typography>
                </Breadcrumbs>
                <Box className={styles.spaceOnTop}>
                    {alertVariant && (
                        <AlertBox msg={alertMsg} variant={alertVariant} />
                    )}
                </Box>
                {!isLoading && job && (
                    <Box className={styles.boxForButton}>
                        <Typography
                            variant="h1"
                            className={styles.pageLevelHeading}
                        >
                            #{jobNumber}
                        </Typography>
                        {job.status === 'WITHDRAWN' && (
                            <Badge
                                startIcon={<CheckCircle />}
                                variant="warning"
                            >
                                Bid Withdrawn
                            </Badge>
                        )}
                    </Box>
                )}
                {isLoading && (
                    <CircularProgress
                        size="lg"
                        variant="indeterminate"
                        title={'Loading'}
                    />
                )}
                {!isLoading && job && (
                    <Grid
                        container
                        spacing={4}
                        className={jobStyles.bidGridSection}
                    >
                        <Grid item sm={12}>
                            <Box className={styles.boxForButton}>
                                <Typography
                                    fontWeight="700"
                                    variant="h2"
                                    color="#000000"
                                >
                                    {job.classification?.classification} -
                                    Department: {job.department}
                                </Typography>
                                {job.status !== 'WITHDRAWN' && (
                                    <Typography fontWeight="700" variant="h4">
                                        Ranked {job.adminRank} out of{' '}
                                        {job.totalActiveBidders} bidders
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                        <Grid className={jobStyles.gridSpan} item sm={12}>
                            <span>{job.facility}</span>
                            <span>Pay Level {job.jobGrade}</span>
                            <span>CIPP - {job.cipp ? 'Yes' : 'No'}</span>
                            <span>CIPP {job.cippPerc}%</span>
                        </Grid>
                        <Grid className={jobStyles.gridSpan} item sm={12}>
                            <span>
                                Closes{' '}
                                {job.endDate
                                    ? DateTime.fromISO(job.endDate).toFormat(
                                          'LL/dd/yy'
                                      )
                                    : ''}{' '}
                            </span>
                            <span>
                                Bid Withdrawal Starts{' '}
                                {job.endDate
                                    ? DateTime.fromISO(job.endDate).toFormat(
                                          'LL/dd/yy'
                                      )
                                    : ''}
                            </span>
                            <span>
                                Bid Withdrawal Ends{' '}
                                {job.endDate
                                    ? DateTime.fromISO(job.endDate)
                                          .plus({ days: job.bidWithdrawalDays })
                                          .toFormat('LL/dd/yy')
                                    : ''}
                            </span>
                        </Grid>
                        {job.additionalDetails && (
                            <Grid item sm={12}>
                                {job.additionalDetails}
                            </Grid>
                        )}

                        <Grid item sm={4}>
                            <div>
                                <span className={jobStyles.bidData}>
                                    Qualifications:{' '}
                                </span>
                                <span>{job.qualifications}</span>
                            </div>
                            <div>
                                <span className={jobStyles.bidData}>
                                    Number of Openings:
                                </span>
                                <span>{job.noOfOpenings}</span>
                            </div>
                            <div>
                                <span className={jobStyles.bidData}>
                                    Supervisor(s):{' '}
                                </span>
                                <span>{job.supervisors}</span>
                            </div>
                            <div>
                                <span className={jobStyles.bidData}>
                                    Over time percentage:{' '}
                                </span>
                                <span>{job.otPerc}%</span>
                            </div>
                            <div>
                                <span className={jobStyles.bidData}>
                                    Green Line:{' '}
                                </span>
                                <span>{job.greenLine}</span>
                            </div>
                            <div>
                                <span className={jobStyles.bidData}>
                                    Req Number:{' '}
                                </span>
                                <span>{job.reqNumber}</span>
                            </div>
                        </Grid>
                        <Grid item sm={4}>
                            <div>
                                <span className={jobStyles.bidData}>
                                    Junior Permanent Seniority 1st:{' '}
                                </span>
                                <span>
                                    {job.seniority1
                                        ? DateTime.fromISO(
                                              job.seniority1
                                          ).toFormat('LL/dd/yy')
                                        : ''}
                                </span>
                            </div>
                            <div>
                                <span className={jobStyles.bidData}>
                                    Junior Permanent Seniority 2nd:{' '}
                                </span>
                                <span>
                                    {job.seniority2
                                        ? DateTime.fromISO(
                                              job.seniority2
                                          ).toFormat('LL/dd/yy')
                                        : ''}
                                </span>
                            </div>
                            <div>
                                <span className={jobStyles.bidData}>
                                    Junior Permanent Seniority 3rd:{' '}
                                </span>
                                <span>
                                    {job.seniority3
                                        ? DateTime.fromISO(
                                              job.seniority3
                                          ).toFormat('LL/dd/yy')
                                        : ''}
                                </span>
                            </div>
                        </Grid>
                        <Grid item sm={4}></Grid>
                    </Grid>
                )}
                {!isLoading && job && job.status !== 'WITHDRAWN' && (
                    <Box mt={2} justifyContent="flex-end" display="flex">
                        <WithdrawBidModal
                            jobNumber={jobNumber}
                            bidId={job.bidId}
                            apiResponse={(resp, errorResponse) => {
                                if (resp === 'error') {
                                    setAlertVariant('error');
                                    setAlertMsg(
                                        errorBannerFromResponse(
                                            errorResponse,
                                            'Due to technical issues, we are unable to withdraw this job bid. Please try again later.'
                                        ).message
                                    );
                                }
                            }}
                        />
                    </Box>
                )}
            </Box>
        </div>
    );
};
